import { Config } from './types';
import { track } from 'shared/util/analytics/track';
import React from 'react';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { buildVideoEventHandlers } from './VideoUpload.util';

const VideoUpload: React.FC<WidgetRenderProps<Config>> = (
  props: WidgetRenderProps<Config>
) => {
  const {
    paddingData,
    borderData,
    lineColor,
    visible,
    isCustomWidget,
  } = props.widgetState.config;

  let image: Image;
  const renderImage =
    image ||
    (props.editing && {
      url:
        'https://cdn-stage.linkhealth.com/site-builder/horizon/video-placeholder.png',
      alt: '',
    });

  // Tracking hook must be called unconditionally (before any return statements)
  const trackVideo = useTrackWidget({
    tracking: (props as any).tracking,
    widgetState: props.widgetState,
    currentPage: props.currentPage,
    site: props.site,
  });

  if (visible === false || isCustomWidget) {
    // Maintain presence of root div for consistency & potential layout calculations while hidden.
    return <div data-test-id="video-upload" />;
  }

  const handlers = buildVideoEventHandlers({
    editing: props.editing,
    widgetState: props.widgetState,
    trackVideo,
  });

  return (
    <div
      style={{
        paddingTop: `${paddingData?.top}px`,
        paddingRight: `${paddingData?.right}px`,
        paddingBottom: `${paddingData?.bottom}px`,
        paddingLeft: `${paddingData?.left}px`,
        borderTop: `${
          borderData?.isTopBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderBottom: `${
          borderData?.isBottomBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderLeft: `${
          borderData?.isLeftBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderRight: `${
          borderData?.isRightBorderVisible ?? true
            ? `${borderData?.thickness}px solid`
            : 'none'
        }`,
        borderColor: lineColor,
        borderRadius: `${borderData?.radius}px`,
        width: '100%',
        height: '100%',
      }}
      data-test-id="video-upload"
    >
      {props.widgetState.config?.file?.url ? (
        <video
          style={{ height: '100%', width: '100%' }}
          controls
          onPlay={e => handlers.onPlay(e.currentTarget)}
          onPause={e => handlers.onPause(e.currentTarget)}
          onSeeked={e => handlers.onSeeked(e.currentTarget)}
          onEnded={e => handlers.onEnded(e.currentTarget)}
        >
          <source src={props.widgetState.config.file.url} />
          {/* Empty data URI track element to satisfy a11y/lint requirement for captions track. */}
          <track kind="captions" src="data:," label="captions" />
        </video>
      ) : (
        <img
          style={{ height: '100%', width: '100%' }}
          src={
            'https://cdn-stage.linkhealth.com/site-builder/horizon/video-placeholder.png'
          }
          alt={renderImage?.alt}
          draggable={false}
        />
      )}
    </div>
  );
};

export default track()(VideoUpload);
